import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEnvelope, faKey, faCheckCircle, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FaTiktok, FaFacebookF, FaLinkedinIn, FaGoogle } from 'react-icons/fa';
import logo from 'assets/images/logo.png';
import { postRequest } from '../../services/api/apiService';

// Decorative platform pills floating in background
const BG_PILLS = [
  { icon: <FaTiktok />, label: 'TikTok Ads', color: '#010101', bg: '#F3F4F6', border: '#D1D5DB', top: '8%', left: '3%', rotate: '-6deg' },
  { icon: <FaFacebookF />, label: 'Meta Ads', color: '#1877f2', bg: '#EEF4FF', border: '#BFDBFE', top: '18%', right: '4%', rotate: '5deg' },
  { icon: <FaLinkedinIn />, label: 'LinkedIn Ads', color: '#0a66c2', bg: '#EFF6FF', border: '#BAE6FD', bottom: '22%', left: '2%', rotate: '4deg' },
  { icon: <FaGoogle />, label: 'Google Ads', color: '#4285f4', bg: '#EFF6FF', border: '#BFDBFE', bottom: '12%', right: '3%', rotate: '-4deg' },
  { icon: '👻', label: 'Snapchat', color: '#8B7500', bg: '#FEFCE8', border: '#FDE68A', top: '55%', left: '1%', rotate: '8deg' },
  { icon: '🛒', label: 'Shopify', color: '#5a7a1a', bg: '#F0FDF4', border: '#BBF7D0', top: '42%', right: '2%', rotate: '-7deg' },
];

const ForgotPassword = () => {
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [resetUrl, setResetUrl] = useState('');
  const [copied, setCopied]     = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await postRequest('/api/auth/forgot-password', { email: email.trim() });
      setSent(true);
      if (res.devResetUrl) setResetUrl(res.devResetUrl);
    } catch (err) {
      const url = (err.config?.baseURL || '') + (err.config?.url || '');
      let detail;
      if (err.response) {
        detail = `HTTP ${err.response.status} sur ${url} — ${JSON.stringify(err.response.data)}`;
      } else if (err.request) {
        detail = `Aucune réponse reçue (réseau/CORS/timeout) sur ${url} — code=${err.code || 'n/a'} — ${err.message}`;
      } else {
        detail = `Erreur avant envoi (${url}) — ${err.message}`;
      }
      console.error('[ForgotPassword] échec de la requête', { url, err });
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(resetUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #F0F4FF 0%, #EEF2FF 50%, #F5F3FF 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @keyframes authFloat { 0%,100%{transform:translateY(0) rotate(var(--r,0deg))} 50%{transform:translateY(-8px) rotate(var(--r,0deg))} }
        .fp-input {
          width: 100%; padding: 12px 14px 12px 42px;
          border: 1.5px solid #E5E7EB; border-radius: 10px;
          font-size: 14px; color: #0F0B2D; background: #F9FAFB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .fp-input::placeholder { color: #9CA3AF; }
        .fp-input:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .fp-input.error { border-color: #EF4444; background: #fff; }
        .fp-btn {
          width: 100%; padding: 13px; border-radius: 10px;
          background: #221C51; color: #fff; font-size: 15px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.2s ease;
        }
        .fp-btn:hover:not(:disabled) { background: #2D2570; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(34,28,81,0.25); }
        .fp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>

      {/* Decorative floating platform pills */}
      {BG_PILLS.map((p, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: p.top, bottom: p.bottom, left: p.left, right: p.right,
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '8px 14px', borderRadius: 10,
          background: p.bg, border: `1px solid ${p.border}`,
          fontSize: 12, fontWeight: 600, color: p.color,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          opacity: 0.85,
          transform: `rotate(${p.rotate})`,
          animation: `authFloat ${5 + i * 0.8}s ease-in-out infinite`,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ fontSize: 14 }}>{p.icon}</span>
          {p.label}
        </div>
      ))}

      {/* Subtle blob background */}
      <div style={{ position: 'absolute', top: '-120px', left: '-120px', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,28,81,0.07) 0%, transparent 60%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-100px', right: '-100px', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 60%)', pointerEvents: 'none' }} />

      {/* Logo */}
      <Link to="/" style={{ marginBottom: 28, display: 'inline-block', position: 'relative', zIndex: 1 }}>
        <img src={logo} alt="OneBotAds" style={{ height: 34 }} />
      </Link>

      {/* Card */}
      <div style={{
        width: '100%', maxWidth: 420,
        background: '#fff', borderRadius: 20,
        border: '1px solid rgba(34,28,81,0.1)',
        padding: '36px 36px 32px',
        boxShadow: '0 8px 40px rgba(34,28,81,0.1), 0 2px 8px rgba(0,0,0,0.05)',
        position: 'relative', zIndex: 1,
      }}>
        <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#6B7280', fontSize: 13, textDecoration: 'none', marginBottom: 24, fontWeight: 500, transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#221C51'}
          onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}
        >
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11 }} /> Back to login
        </Link>

        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#EEF2FF', border: '1.5px solid #C7D2FE', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
          <FontAwesomeIcon icon={faKey} style={{ fontSize: 18, color: '#221C51' }} />
        </div>

        {!sent ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0F0B2D', marginBottom: 8, letterSpacing: '-0.02em' }}>Forgot your password?</h1>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.6 }}>
              Enter your email and we'll generate a reset link for you.
            </p>
            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Email address</label>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="you@example.com" className={`fp-input${error ? ' error' : ''}`} />
              </div>
              {error && <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 14 }}>{error}</p>}
              {!error && <div style={{ marginBottom: 20 }} />}
              <button type="submit" disabled={loading} className="fp-btn">
                {loading ? 'Generating link…' : 'Send Reset Link'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#ECFDF5', border: '1.5px solid #A7F3D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#059669', fontSize: 15 }} />
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F0B2D', letterSpacing: '-0.02em' }}>Reset link generated!</h1>
            </div>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 20, lineHeight: 1.6 }}>
              In production this would be sent by email. For now, use the link below:
            </p>
            {resetUrl && (
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
                <p style={{ fontSize: 11, color: '#059669', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Reset link (dev mode)</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: 12, color: '#6B7280', wordBreak: 'break-all', fontFamily: 'monospace' }}>{resetUrl}</span>
                  <button onClick={handleCopy} style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, background: copied ? '#ECFDF5' : '#fff', border: `1px solid ${copied ? '#A7F3D0' : '#E5E7EB'}`, color: copied ? '#059669' : '#374151', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    <FontAwesomeIcon icon={faCopy} style={{ marginRight: 4 }} />{copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
            <Link to={resetUrl || '/login'} style={{ display: 'block', textAlign: 'center', padding: '13px', borderRadius: 10, background: '#221C51', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Open Reset Page →
            </Link>
            <button onClick={() => { setSent(false); setEmail(''); setResetUrl(''); }} style={{ display: 'block', width: '100%', marginTop: 10, padding: '10px', background: 'none', border: 'none', color: '#9CA3AF', fontSize: 13, cursor: 'pointer' }}>
              Try a different email
            </button>
          </>
        )}
      </div>

      <p style={{ marginTop: 24, fontSize: 12, color: '#9CA3AF', position: 'relative', zIndex: 1 }}>
        © 2026 OneBotAds. All rights reserved.
      </p>
    </div>
  );
};

export default ForgotPassword;
